import { motionPolicy } from './motion-policy';
import { EntityKind, type PrimaryStore } from './primary-store';
import type { BodyComposition } from './composition-definition';
import { BODY_DEFINITIONS, type BodyAppearance } from './body-registry';
export { BodySurfaceClass, type BodyAppearance } from './body-registry';

export interface MoonSpec {
  readonly index: number;
  readonly orbitRadius: number;
  readonly bodyRadius: number;
  readonly phase: number;
  readonly angularSpeed: number;
  readonly mass: number;
  readonly orbitalMass: number;
}

export interface PlanetSpec {
  readonly index: number;
  readonly orbitRadius: number;
  readonly phase: number;
  readonly mass: number;
  readonly orbitalMass: number;
  readonly bodyRadius: number;
  /** Legacy starting metadata; runtime drag/avoidance use atmosphere-profile.ts. */
  readonly atmosphereHeight: number;
  readonly atmosphereDamping: number;
  readonly moons: readonly MoonSpec[];
}

/**
 * Capability-based description shared by every non-star spherical body.
 * `parentIndex` controls orbital hierarchy only; it does not grant physics,
 * collision, lighting, or ecology privileges.
 */
export interface CelestialBodySpec {
  readonly index: number;
  readonly parentIndex: number;
  readonly orbitRadius: number;
  readonly phase: number;
  readonly mass: number;
  readonly orbitalMass: number;
  readonly bodyRadius: number;
  /** Legacy starting metadata; runtime drag/avoidance use atmosphere-profile.ts. */
  readonly atmosphereHeight: number;
  readonly atmosphereDamping: number;
  readonly supportsLife: boolean;
  readonly supportsAlgae: boolean;
  readonly appearance: BodyAppearance;
  readonly composition: BodyComposition;
}

export const BODY_APPEARANCES = BODY_DEFINITIONS.map(body => body.appearance);

export const STAR_INDEX = 0;
export const STAR_MASS = 4_500;
export const STAR_COLLISION_RADIUS = 0.085;
export const MOONS_ENABLED_BY_DEFAULT = true;
export const CORE_CELESTIAL_ENTITY_COUNT = 9;
export const CELESTIAL_ENTITY_COUNT = 17;
export const CELESTIAL_ENTITY_COUNT_WITH_MOONS = CELESTIAL_ENTITY_COUNT;
export const ASTEROID_COUNT = 1_024;
export const ASTEROID_MASS = 0.0025;
export const SOLAR_SYSTEM_DOMAIN_HALF_SIZE = 5;
export const ASTEROID_BELT_INNER_RADIUS = 1.72;
export const ASTEROID_BELT_OUTER_RADIUS = 2.18;
export const ECOSYSTEM_GRAVITATIONAL_CONSTANT = 0.000002;

export const SOLAR_ORBIT_SPEED = Math.sqrt(
  ECOSYSTEM_GRAVITATIONAL_CONSTANT * STAR_MASS,
);

export const PLANET_SPECS: readonly PlanetSpec[] = BODY_DEFINITIONS
  .filter(body => body.parentIndex === STAR_INDEX)
  .map(body => ({ ...body, moons: BODY_DEFINITIONS.filter(moon => moon.parentIndex === body.index) }));

export const CELESTIAL_BODY_SPECS: readonly CelestialBodySpec[] = BODY_DEFINITIONS.map(body => ({
  ...body,
  supportsLife: supportsAquaticLife(body.composition),
  supportsAlgae: supportsAquaticLife(body.composition),
}));

export const LIFE_SUPPORTING_BODY_SLOTS: readonly number[] =
  CELESTIAL_BODY_SPECS.flatMap((body, slot) =>
    body.supportsLife ? [slot] : [],
  );

export function bodySpecForIndex(index: number): CelestialBodySpec | undefined {
  return CELESTIAL_BODY_SPECS[index - 1];
}

function supportsAquaticLife(composition: BodyComposition): boolean {
  return composition.layer === 'surface' &&
    (composition.materials.find(stock => stock.material === 'water')?.phases.liquid ?? 0) > 0.03 &&
    (composition.materials.find(stock => stock.material === 'carbonDioxide')?.phases.gas ?? 0) > 0;
}

export function seedCelestialBodies(
  store: PrimaryStore,
  firstLineage: number,
  includeMoons = MOONS_ENABLED_BY_DEFAULT,
  asteroidCount = 0,
): number {
  let nextLineage = firstLineage;
  store.add(
    EntityKind.Star,
    0,
    0,
    STAR_MASS,
    STAR_MASS,
    0,
    0,
    0,
    nextLineage,
  );
  nextLineage += 1;

  for (const planet of PLANET_SPECS) {
    const cosine = Math.cos(planet.phase);
    const sine = Math.sin(planet.phase);
    store.add(
      EntityKind.CelestialBody,
      cosine * planet.orbitRadius,
      sine * planet.orbitRadius,
      planet.mass,
      planet.mass,
      -sine * SOLAR_ORBIT_SPEED,
      cosine * SOLAR_ORBIT_SPEED,
      0,
      nextLineage,
    );
    nextLineage += 1;
  }

  if (includeMoons) {
    for (const planet of PLANET_SPECS) {
      for (const moon of planet.moons) {
        const parentX = store.x[planet.index] ?? 0;
        const parentY = store.y[planet.index] ?? 0;
        const parentVx = store.vx[planet.index] ?? 0;
        const parentVy = store.vy[planet.index] ?? 0;
        const cosine = Math.cos(moon.phase);
        const sine = Math.sin(moon.phase);
        const direction = Math.sign(moon.angularSpeed) || 1;
        const localSpeed = planetaryCircularSpeed(planet) * direction;
        store.add(
          EntityKind.CelestialBody,
          parentX + cosine * moon.orbitRadius,
          parentY + sine * moon.orbitRadius,
          moon.mass,
          moon.mass,
          parentVx - sine * localSpeed,
          parentVy + cosine * localSpeed,
          0,
          nextLineage,
        );
        nextLineage += 1;
      }
    }
  }

  const safeAsteroidCount = Math.max(
    0,
    Math.min(Math.floor(asteroidCount), store.capacity - store.count),
  );
  for (let ordinal = 0; ordinal < safeAsteroidCount; ordinal += 1) {
    const radialMix = hashUnit(ordinal + 17);
    const radius =
      ASTEROID_BELT_INNER_RADIUS +
      (ASTEROID_BELT_OUTER_RADIUS - ASTEROID_BELT_INNER_RADIUS) * radialMix;
    const angle =
      ordinal * 2.399963229728653 + hashUnit(ordinal + 911) * 0.14;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    store.add(
      EntityKind.Asteroid,
      cosine * radius,
      sine * radius,
      ASTEROID_MASS * (0.65 + hashUnit(ordinal + 307) * 0.7),
      0,
      -sine * SOLAR_ORBIT_SPEED,
      cosine * SOLAR_ORBIT_SPEED,
      0,
      nextLineage,
    );
    nextLineage += 1;
  }

  makeCelestialStateBarycentric(store, store.count);
  recenterOnPrimaryStar(store);
  return nextLineage;
}

/**
 * Computes the hierarchical massive-body field for the celestial prefix.
 * Asteroids are explicit test particles: they respond to the star and the
 * registered bodies, but neither they nor ecological matter back-react on
 * that massive hierarchy. Parent-to-moon habitat boosts are a deliberate
 * hierarchical approximation, not a reciprocal Newtonian N-body system.
 */
export function writeCelestialAccelerations(
  store: PrimaryStore,
  celestialCount: number,
): void {
  const safeCount = Math.min(celestialCount, store.count);
  let firstAsteroid = safeCount;
  for (let index = 0; index < safeCount; index += 1) {
    if (store.kind[index] === EntityKind.Asteroid) {
      firstAsteroid = index;
      break;
    }
  }
  for (let target = 0; target < safeCount; target += 1) {
    const targetKind = store.kind[target] ?? EntityKind.Inactive;
    if (!isPhysicalCelestial(targetKind)) {
      continue;
    }
    let accelerationX = 0;
    let accelerationY = 0;
    const sourceCount = firstAsteroid;
    for (let source = 0; source < sourceCount; source += 1) {
      if (source === target) {
        continue;
      }
      const sourceMass = celestialSourceMass(store, source, target);
      if (sourceMass <= 0) {
        continue;
      }
      const dx = (store.x[source] ?? 0) - (store.x[target] ?? 0);
      const dy = (store.y[source] ?? 0) - (store.y[target] ?? 0);
      const radiusSquared = Math.max(0.003 ** 2, dx * dx + dy * dy);
      const scale =
        (ECOSYSTEM_GRAVITATIONAL_CONSTANT * sourceMass) / radiusSquared;
      accelerationX += dx * scale;
      accelerationY += dy * scale;
    }
    store.ax[target] = accelerationX;
    store.ay[target] = accelerationY;
  }
}

/** Symplectic integration keeps near-circular starting orbits bounded. */
export function integrateCelestialBodies(
  store: PrimaryStore,
  celestialCount: number,
  deltaSeconds: number,
): void {
  const safeDelta = Math.max(0, deltaSeconds);
  const safeCount = Math.min(celestialCount, store.count);
  for (let index = 0; index < safeCount; index += 1) {
    if (!isPhysicalCelestial(store.kind[index] ?? EntityKind.Inactive)) {
      continue;
    }
    store.vx[index] = (store.vx[index] ?? 0) + (store.ax[index] ?? 0) * safeDelta;
    store.vy[index] = (store.vy[index] ?? 0) + (store.ay[index] ?? 0) * safeDelta;
    store.x[index] = (store.x[index] ?? 0) + (store.vx[index] ?? 0) * safeDelta;
    store.y[index] = (store.y[index] ?? 0) + (store.vy[index] ?? 0) * safeDelta;
    store.age[index] = (store.age[index] ?? 0) + safeDelta;
  }
  recenterOnPrimaryStar(store);
}

export function planetSpecForSlot(planetSlot: number): PlanetSpec | undefined {
  return PLANET_SPECS[planetSlot];
}

export function bodySpecForSlot(bodySlot: number): CelestialBodySpec | undefined {
  return CELESTIAL_BODY_SPECS[bodySlot];
}

export function writePlanetarySpawn(
  store: PrimaryStore,
  kind:
    | EntityKind.Algae
    | EntityKind.Herbivore
    | EntityKind.Predator
    | EntityKind.Scavenger,
  planetSlot: number,
  angle: number,
  radialMix: number,
  output: Float64Array,
): void {
  const planet = bodySpecForSlot(planetSlot) ?? CELESTIAL_BODY_SPECS[0];
  if (planet === undefined) {
    output.fill(0);
    return;
  }
  const [minimumRadius, maximumRadius] = planetaryBand(kind, planet);
  const radius =
    minimumRadius + (maximumRadius - minimumRadius) * clamp01(radialMix);
  const localSpeed = planetaryCircularSpeed(planet);
  const centerX = store.x[planet.index] ?? 0;
  const centerY = store.y[planet.index] ?? 0;
  const planetVx = store.vx[planet.index] ?? 0;
  const planetVy = store.vy[planet.index] ?? 0;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  output[0] = centerX + cosine * radius;
  output[1] = centerY + sine * radius;
  output[2] = planetVx - sine * localSpeed;
  output[3] = planetVy + cosine * localSpeed;
}

export function closestBodySpec(
  store: PrimaryStore,
  x: number,
  y: number,
): CelestialBodySpec | undefined {
  let closest: CelestialBodySpec | undefined;
  let strongestField = 0;
  for (const planet of CELESTIAL_BODY_SPECS) {
    const kind = store.kind[planet.index] ?? EntityKind.Inactive;
    if (kind !== EntityKind.Planet && kind !== EntityKind.Moon) {
      continue;
    }
    const dx = (store.x[planet.index] ?? 0) - x;
    const dy = (store.y[planet.index] ?? 0) - y;
    const distanceSquared = dx * dx + dy * dy;
    const fieldStrength =
      planet.mass / Math.max(planet.bodyRadius ** 2, distanceSquared);
    if (fieldStrength > strongestField) {
      strongestField = fieldStrength;
      closest = planet;
    }
  }
  return closest;
}


/** @deprecated Use closestBodySpec; kept for API compatibility. */
export const closestPlanetSpec = closestBodySpec;

/** Adds the short-range habitat gravity omitted from the global orbital mass. */
export function accumulateLocalPlanetGravity(
  store: PrimaryStore,
  index: number,
  output: Float64Array,
): void {
  const planet = closestBodySpec(
    store,
    store.x[index] ?? 0,
    store.y[index] ?? 0,
  );
  if (planet === undefined) {
    return;
  }
  accumulateBodyGravity(store, index, planet, output, true);
  if (planet.parentIndex !== STAR_INDEX) {
    const parent = bodySpecForIndex(planet.parentIndex);
    if (parent !== undefined) {
      // A moon and anything orbiting it must share the parent's full
      // acceleration. Omitting this term tears satellites out of the moon's
      // frame even when their moon-relative velocity is exactly circular.
      accumulateBodyGravity(store, index, parent, output, false);
    }
  }
}

function accumulateBodyGravity(
  store: PrimaryStore,
  index: number,
  planet: CelestialBodySpec,
  output: Float64Array,
  fadeAtHabitatEdge: boolean,
): void {
  const dx = (store.x[planet.index] ?? 0) - (store.x[index] ?? 0);
  const dy = (store.y[planet.index] ?? 0) - (store.y[index] ?? 0);
  const distanceSquared = dx * dx + dy * dy;
  if (fadeAtHabitatEdge && distanceSquared > 0.18 ** 2) {
    return;
  }
  const distance = Math.sqrt(distanceSquared);
  const fade = fadeAtHabitatEdge
    ? clamp01((distance - 0.11) / 0.07)
    : 0;
  const localFieldWeight = 1 - fade * fade * (3 - 2 * fade);
  const scale =
    (ECOSYSTEM_GRAVITATIONAL_CONSTANT *
      (planet.mass - planet.orbitalMass) *
      localFieldWeight) /
    Math.max(0.003 ** 2, distanceSquared);
  output[0] = (output[0] ?? 0) + dx * scale;
  output[1] = (output[1] ?? 0) + dy * scale;
}

function makeCelestialStateBarycentric(
  store: PrimaryStore,
  celestialCount: number,
): void {
  let weightedX = 0;
  let weightedY = 0;
  let weightedVx = 0;
  let weightedVy = 0;
  let totalMass = 0;
  for (let index = 0; index < celestialCount; index += 1) {
    const mass = orbitalMassForIndex(store, index);
    weightedX += (store.x[index] ?? 0) * mass;
    weightedY += (store.y[index] ?? 0) * mass;
    weightedVx += (store.vx[index] ?? 0) * mass;
    weightedVy += (store.vy[index] ?? 0) * mass;
    totalMass += mass;
  }
  const centerX = weightedX / Math.max(1e-9, totalMass);
  const centerY = weightedY / Math.max(1e-9, totalMass);
  const centerVx = weightedVx / Math.max(1e-9, totalMass);
  const centerVy = weightedVy / Math.max(1e-9, totalMass);
  for (let index = 0; index < celestialCount; index += 1) {
    store.x[index] = (store.x[index] ?? 0) - centerX;
    store.y[index] = (store.y[index] ?? 0) - centerY;
    store.vx[index] = (store.vx[index] ?? 0) - centerVx;
    store.vy[index] = (store.vy[index] ?? 0) - centerVy;
  }
}

/**
 * Expresses the complete simulation in the primary star's instantaneous frame.
 * Relative positions and velocities are preserved, so the N-body calculation
 * can remain general while the current single-star presentation stays centered.
 */
export function recenterOnPrimaryStar(store: PrimaryStore): void {
  if (store.kind[STAR_INDEX] !== EntityKind.Star) return;
  const starX = store.x[STAR_INDEX] ?? 0;
  const starY = store.y[STAR_INDEX] ?? 0;
  const starVx = store.vx[STAR_INDEX] ?? 0;
  const starVy = store.vy[STAR_INDEX] ?? 0;
  if (starX === 0 && starY === 0 && starVx === 0 && starVy === 0) return;
  for (let index = 0; index < store.count; index += 1) {
    if ((store.kind[index] ?? EntityKind.Inactive) === EntityKind.Inactive) {
      continue;
    }
    store.x[index] = (store.x[index] ?? 0) - starX;
    store.y[index] = (store.y[index] ?? 0) - starY;
    store.vx[index] = (store.vx[index] ?? 0) - starVx;
    store.vy[index] = (store.vy[index] ?? 0) - starVy;
    if ((store.memoryTimer[index] ?? 0) > 0 && (store.memoryBody[index] ?? -1) < 0) {
      store.memoryX[index] = (store.memoryX[index] ?? 0) - starX + starVx * (store.memoryAge[index] ?? 0);
      store.memoryY[index] = (store.memoryY[index] ?? 0) - starY + starVy * (store.memoryAge[index] ?? 0);
      store.memoryVx[index] = (store.memoryVx[index] ?? 0) - starVx;
      store.memoryVy[index] = (store.memoryVy[index] ?? 0) - starVy;
    }
  }
  store.x[STAR_INDEX] = 0;
  store.y[STAR_INDEX] = 0;
  store.vx[STAR_INDEX] = 0;
  store.vy[STAR_INDEX] = 0;
}

function celestialSourceMass(
  store: PrimaryStore,
  source: number,
  target: number,
): number {
  const sourceKind = store.kind[source] ?? EntityKind.Inactive;
  if (sourceKind === EntityKind.Planet || sourceKind === EntityKind.Moon) {
    const targetBody = bodySpecForIndex(target);
    const sourceBody = bodySpecForIndex(source);
    if (sourceBody === undefined) {
      return 0;
    }
    return targetBody?.parentIndex === source
      ? sourceBody.mass
      : sourceBody.orbitalMass;
  }
  return orbitalMassForIndex(store, source);
}

function orbitalMassForIndex(store: PrimaryStore, index: number): number {
  const kind = store.kind[index] ?? EntityKind.Inactive;
  if (kind === EntityKind.Star) {
    return STAR_MASS;
  }
  if (kind === EntityKind.Planet || kind === EntityKind.Moon) {
    return bodySpecForIndex(index)?.orbitalMass ?? 0;
  }
  return kind === EntityKind.Asteroid ? store.mass[index] ?? ASTEROID_MASS : 0;
}

function isPhysicalCelestial(kind: EntityKind): boolean {
  return motionPolicy(kind).hierarchyIntegration;
}

function hashUnit(seed: number): number {
  let value = Math.imul(seed ^ 0x9e37_79b9, 0x85eb_ca6b) >>> 0;
  value ^= value >>> 13;
  value = Math.imul(value, 0xc2b2_ae35) >>> 0;
  return ((value ^ (value >>> 16)) >>> 0) / 0x1_0000_0000;
}

function planetaryBand(
  kind:
    | EntityKind.Algae
    | EntityKind.Herbivore
    | EntityKind.Predator
    | EntityKind.Scavenger,
  planet: Pick<CelestialBodySpec, 'bodyRadius'>,
): readonly [number, number] {
  const moonScale = planet.bodyRadius <= 0.008;
  if (kind === EntityKind.Algae) {
    return moonScale
      ? [planet.bodyRadius + 0.02, planet.bodyRadius + 0.03]
      : [planet.bodyRadius + 0.006, planet.bodyRadius + 0.018];
  }
  if (kind === EntityKind.Herbivore) {
    return moonScale
      ? [planet.bodyRadius + 0.036, planet.bodyRadius + 0.046]
      : [planet.bodyRadius + 0.03, planet.bodyRadius + 0.04];
  }
  if (kind === EntityKind.Scavenger) {
    return moonScale
      ? [planet.bodyRadius + 0.048, planet.bodyRadius + 0.056]
      : [planet.bodyRadius + 0.043, planet.bodyRadius + 0.046];
  }
  return moonScale
    ? [planet.bodyRadius + 0.06, planet.bodyRadius + 0.068]
    : [planet.bodyRadius + 0.049, planet.bodyRadius + 0.052];
}

export function planetaryCircularSpeed(
  planet: Pick<CelestialBodySpec, 'mass'>,
): number {
  return Math.sqrt(ECOSYSTEM_GRAVITATIONAL_CONSTANT * planet.mass);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
